import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  SearchOutlined,
  SolutionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { JobContent } from '../jobs/models';
import { JobService } from '../jobs/services/job.service';
import {
  PublishedRecommendation,
  SiteRecommendationService,
} from '../recommendations/site-recommendation.service';
import RecommendationCarousel from '../recommendations/RecommendationCarousel';
import './home.css';
import { useHomeScrollReveal } from './useHomeScrollReveal';

const latestSkills = (job: JobContent): string[] =>
  (job.skills ?? []).slice(0, 2).map(({ skillName }) => skillName);

const locationLabel = (job: JobContent): string =>
  [job.location, job.countries?.join(' / ')].filter(Boolean).join(' · ') ||
  'Location flexible';

interface HomeRecommendationSectionProps {
  className?: string;
  eyebrow: string;
  title: string;
  description: string;
  recommendations: PublishedRecommendation[];
}

const HomeRecommendationSection: React.FC<HomeRecommendationSectionProps> = ({
  className = '',
  eyebrow,
  title,
  description,
  recommendations,
}) => (
  <section className={`home-recommendations ${className}`.trim()}>
    <div className="public-content-rail">
      <header
        className="home-section-header home-section-header--centered home-recommendations__header"
        data-home-reveal
      >
        <div>
          <p className="home-eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </header>
      <RecommendationCarousel
        recommendations={recommendations}
        label={`${title} recommendations`}
      />
    </div>
  </section>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const homeRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState('');
  const [latestJobs, setLatestJobs] = useState<JobContent[]>([]);
  const [recommendations, setRecommendations] = useState<
    PublishedRecommendation[]
  >([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  useHomeScrollReveal(homeRef, `${jobsLoading}:${recommendations.length}`);

  useEffect(() => {
    document.title = 'ITJobOpportunities | Tech careers and hiring';

    const load = async () => {
      const [jobsResult, recommendationsResult] = await Promise.allSettled([
        JobService.GetJobs([], '', 1),
        SiteRecommendationService.published(),
      ]);

      if (jobsResult.status === 'fulfilled') {
        setLatestJobs((jobsResult.value.content ?? []).slice(0, 3));
      }
      if (recommendationsResult.status === 'fulfilled') {
        setRecommendations(recommendationsResult.value);
      }
      setJobsLoading(false);
    };

    void load();
  }, []);

  const searchJobs = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    navigate(trimmed ? `/jobs?q=${encodeURIComponent(trimmed)}` : '/jobs');
  };

  const recruiterRecommendations = recommendations.filter(
    ({ audience }) => !audience || audience === 'RECRUITER'
  );
  const jobSeekerRecommendations = recommendations.filter(
    ({ audience }) => audience === 'JOB_SEEKER'
  );

  return (
    <main ref={homeRef} className="home-page">
      <section className="home-hero">
        <div className="public-content-rail home-hero__inner">
          <p className="home-eyebrow">Tech Careers and Hiring, Connected</p>
          <h1>
            Find Work That Fits.
            <span>Hire People Who Move It Forward.</span>
          </h1>
          <p className="home-hero__lead">
            Explore technology opportunities across Latin America or bring your
            next job to a focused recruiting community.
          </p>
          <form className="home-search" onSubmit={searchJobs}>
            <label htmlFor="home-job-search">
              <SearchOutlined aria-hidden="true" />
              <span className="sr-only">Job, skill or keyword</span>
              <input
                id="home-job-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Job, skill or keyword"
              />
            </label>
            <button type="submit">Search Jobs</button>
          </form>
          <div className="home-popular" aria-label="Popular searches">
            <span>Popular:</span>
            {['React', 'Data', 'Cloud', 'QA'].map((term) => (
              <Link key={term} to={`/jobs?q=${encodeURIComponent(term)}`}>
                {term}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="public-content-rail home-opportunity-layout">
        <div className="home-latest" data-home-reveal>
          <header className="home-section-header">
            <div>
              <p className="home-eyebrow">Latest Opportunities</p>
              <h2>Fresh Jobs From Teams Hiring Now</h2>
            </div>
            <Link to="/jobs" className="home-inline-link">
              View All Jobs <ArrowRightOutlined />
            </Link>
          </header>

          {jobsLoading ? (
            <div className="home-jobs-loading" role="status">
              <span className="home-loading-dot" aria-hidden="true" />
              Loading opportunities…
            </div>
          ) : latestJobs.length ? (
            <div className="home-job-list">
              {latestJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="home-job-row"
                >
                  <span className="home-company-mark" aria-hidden="true">
                    {(job.company || job.name || 'J').charAt(0).toUpperCase()}
                  </span>
                  <span className="home-job-row__main">
                    <strong>{job.name}</strong>
                    <small>
                      {job.company || 'Hiring company'} · {locationLabel(job)}
                    </small>
                  </span>
                  <span className="home-job-skills" aria-label="Skills">
                    {latestSkills(job).map((skill) => (
                      <span key={skill}>{skill}</span>
                    ))}
                  </span>
                  <ArrowRightOutlined className="home-job-arrow" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="home-jobs-empty">
              <FileSearchOutlined />
              <div>
                <strong>More Opportunities Are on the Way</strong>
                <span>Browse the directory for the latest available jobs.</span>
              </div>
            </div>
          )}
        </div>

        <aside className="home-recruiter-panel" data-home-reveal>
          <p className="home-eyebrow">For Recruiting Teams</p>
          <h2>From Job Post to Shortlist, in One Workspace.</h2>
          <ul>
            <li>
              <CheckCircleOutlined /> Publish focused technology openings
            </li>
            <li>
              <CheckCircleOutlined /> Review applicants by hiring stage
            </li>
            <li>
              <CheckCircleOutlined /> Keep candidate communication clear
            </li>
          </ul>
          <Link to="/sign-up">Explore Recruiting Tools</Link>
        </aside>
      </section>

      <section className="home-how-it-works">
        <div className="public-content-rail home-path-layout">
          <header className="home-path-intro" data-home-reveal>
            <h2>A Simpler Way to Discover and Hire Tech Talent</h2>
            <p>
              Candidates and recruiting teams share one focused flow—less noise
              before the application, more clarity after it.
            </p>
            <nav
              className="home-path-actions"
              aria-label="Explore the platform"
            >
              <Link to="/jobs">
                Browse Open Jobs <ArrowRightOutlined aria-hidden />
              </Link>
              <Link to="/sign-up">
                Post a Job <ArrowRightOutlined aria-hidden />
              </Link>
            </nav>
          </header>
          <div className="home-value-list">
            <article data-home-reveal>
              <span className="home-value-icon" aria-hidden>
                <FileSearchOutlined />
              </span>
              <div>
                <h3>Discover With Context</h3>
                <p>
                  Find focused technology jobs—or publish one—with the detail
                  needed to decide quickly.
                </p>
              </div>
            </article>
            <article data-home-reveal>
              <span className="home-value-icon" aria-hidden>
                <TeamOutlined />
              </span>
              <div>
                <h3>Connect With Confidence</h3>
                <p>
                  Candidates apply directly while recruiters review every
                  profile inside the ATS.
                </p>
              </div>
            </article>
            <article data-home-reveal>
              <span className="home-value-icon" aria-hidden>
                <SolutionOutlined />
              </span>
              <div>
                <h3>Move Forward Clearly</h3>
                <p>
                  Keep decisions, communication, and next steps visible to
                  everyone involved.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {recruiterRecommendations.length > 0 ? (
        <HomeRecommendationSection
          eyebrow="From Recruiting Teams"
          title="Recommended by Recruiters Who Hire Tech Talent"
          description="Perspectives from professionals using ITJobOpportunities to connect focused jobs with qualified candidates."
          recommendations={recruiterRecommendations}
        />
      ) : null}

      {jobSeekerRecommendations.length > 0 ? (
        <HomeRecommendationSection
          className="home-recommendations--job-seekers"
          eyebrow="From Job Seekers"
          title="Recommended by People Building Their Tech Careers"
          description="Experiences from candidates discovering opportunities, applying with context, and taking their next step."
          recommendations={jobSeekerRecommendations}
        />
      ) : null}
    </main>
  );
};

export default Home;
